import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SettingsSubPageWrapper } from '../Settings';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Printer, Plus, Edit, Trash2, Wifi, AlertCircle, FileCheck } from 'lucide-react';
import { QzTrayPanel } from '../../components/QzTrayPanel';
import { DocketLayoutPanel } from '../../components/DocketLayoutPanel';
import { useAuth } from '../../App';
import { allowedBranches } from '../../lib/perms';
import { printTestPage, qzErrorMessage } from '../../lib/qzPrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrintersSettings() {
  const { user } = useAuth();
  const [printers, setPrinters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    branch_id: '', name: '', ip_address: '', port: 9100, paper_width: 80, is_default: false
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [printersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/printers`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true })
      ]);
      setPrinters(printersRes.data);
      setBranches(allowedBranches(user, branchesRes.data));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.branch_id) { toast.error('Please select a branch'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/printers/${editing.printer_id}`, formData, { withCredentials: true });
        toast.success('Printer updated');
      } else {
        await axios.post(`${API}/printers`, formData, { withCredentials: true });
        toast.success('Printer added');
      }
      setIsModalOpen(false);
      setEditing(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save printer');
    }
  };

  const resetForm = () => {
    setFormData({ branch_id: branches.length === 1 ? branches[0].branch_id : '', name: '', ip_address: '', port: 9100, paper_width: 80, is_default: false });
  };

  const handleEdit = (printer) => {
    setEditing(printer);
    setFormData({
      branch_id: printer.branch_id || '',
      name: printer.name || '',
      ip_address: printer.ip_address || '',
      port: printer.port || 9100,
      paper_width: printer.paper_width || 80,
      is_default: printer.is_default || false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this printer?')) return;
    try {
      await axios.delete(`${API}/printers/${id}`, { withCredentials: true });
      toast.success('Printer deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete printer'); }
  };

  const openNew = () => {
    setEditing(null);
    resetForm();
    setIsModalOpen(true);
  };

  const handleTestPrint = async (printer) => {
    try {
      await printTestPage(printer.printer_id);
      toast.success(`Test page sent to ${printer.name}`);
    } catch (e) { toast.error(qzErrorMessage(e)); }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_id === branchId);
    return branch?.name || 'Unknown';
  };

  if (loading) {
    return (
      <SettingsSubPageWrapper title="Printers">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SettingsSubPageWrapper>
    );
  }

  return (
    <SettingsSubPageWrapper title="Printers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Printers
          </h1>
          <p className="text-muted-foreground mt-1">Configure receipt printers for order printing</p>
        </div>
        <Button onClick={openNew} data-testid="add-printer-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Printer
        </Button>
      </div>

      <QzTrayPanel />

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">80mm Receipt Printer Setup</h3>
              <p className="text-sm text-blue-700 mt-1">
                Most 80mm thermal printers use port 9100 for raw printing (QZ Tray). Give every printer a static IP in your router.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {printers.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Printer className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No printers configured</h3>
              <p className="text-muted-foreground mb-4">Add a receipt printer to enable order printing</p>
              <Button onClick={openNew}><Plus className="w-4 h-4 mr-2" />Add Printer</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Printer Name</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Port</TableHead>
                  <TableHead>Paper</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {printers.map((printer) => (
                  <TableRow key={printer.printer_id} data-testid={`printer-row-${printer.printer_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Printer className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{printer.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getBranchName(printer.branch_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Wifi className="w-4 h-4 text-green-500" />
                        <code className="text-sm bg-muted px-2 py-0.5 rounded">{printer.ip_address}</code>
                      </div>
                    </TableCell>
                    <TableCell>{printer.port}</TableCell>
                    <TableCell>{printer.paper_width}mm</TableCell>
                    <TableCell>
                      {printer.is_default && <Badge variant="default">Default</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" title="Print test page" onClick={() => handleTestPrint(printer)} data-testid={`test-print-${printer.printer_id}`}>
                          <FileCheck className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(printer)} data-testid={`edit-printer-${printer.printer_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(printer.printer_id)} data-testid={`delete-printer-${printer.printer_id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {user?.role === 'master_admin' && <DocketLayoutPanel printers={printers} />}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Printer' : 'Add New Printer'}</DialogTitle>
            <DialogDescription>Network receipt printer with a static IP.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="p-name">Printer Name *</Label>
              <Input id="p-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Kitchen Printer" required data-testid="printer-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Branch *</Label>
              <Select value={formData.branch_id} onValueChange={(value) => setFormData({ ...formData, branch_id: value })} disabled={branches.length === 1}>
                <SelectTrigger data-testid="printer-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (<SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="p-ip">IP Address *</Label>
                <Input id="p-ip" value={formData.ip_address} onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })} placeholder="192.168.1.100" required data-testid="printer-ip-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-port">Port</Label>
                <Input id="p-port" type="number" value={formData.port} onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 9100 })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Paper Width (mm)</Label>
              <Select value={formData.paper_width.toString()} onValueChange={(value) => setFormData({ ...formData, paper_width: parseInt(value) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58">58mm</SelectItem>
                  <SelectItem value="80">80mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="p-default">Set as Default</Label>
                <p className="text-xs text-muted-foreground">Use this printer by default for the selected branch</p>
              </div>
              <Switch id="p-default" checked={formData.is_default} onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-printer-btn">{editing ? 'Update' : 'Add'} Printer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsSubPageWrapper>
  );
}
